/* Redirect-to-NL. Business rule: for a visitor whose real IP is Germany AND who is currently
   browsing the German version of the storefront, during THIS feature's own configured time
   window, a DE-restricted variant's own add-to-cart button is swapped for a "Meer Info" link
   (a real <a href>, already rendered server-side by Liquid, pointing at that same variant's
   default/Dutch product page) - clicking the collection grid's image/name link, or the PDP
   variant swiper's own clickable card, does the same via a JS-intercepted redirect (those
   links' real href is the current-language product page, so they can't be pre-rendered
   correctly for a restricted visitor the way the Meer Info link can). The PDP's main
   "Toevoegen" button (whichever variant is currently selected) still redirects on click/submit
   as before, unchanged - see the click/submit interceptors further down this file. No
   add-to-cart happens for a restricted variant either way.

   Country lookup goes through window.getVisitorCountry() (defined in assets/theme.js) - a
   shared, memoized fetch also used by ip-restrict.js and auto-language-redirect.js, so all 3
   features together ever trigger at most one real network request per page load, instead of
   one each (the browser's own HTTP cache was assumed to collapse 3 separate fetches to the
   same URL into one, but was confirmed live not to actually do that - this shared function is
   the real fix). Everything else about this feature stays independent from ip-restrict.js -
   no shared restriction logic, rules, or state - only the country lookup itself is shared.

   This redirect only ever runs when Theme settings > Restriction > "IP-based restriction
   behavior" is set to "redirect_nl" (restrict_mode) - the same setting that also controls
   ip-restrict.js's hide+message behavior when set to "restrict_ip" instead. The two are
   mutually exclusive by construction (one 3-way toggle, never both active at once), and they
   now share the exact same day/hour schedule (Theme settings > Restriction's
   monday_start_hour, etc.) rather than each having its own independent window.

   window.applyRedirectNl() is exposed (mirroring window.applyIpRestriction) so any code that
   injects new DE-restricted markup after page load - currently assets/related-products.js,
   right after its AJAX fetch - can re-run the scan against the freshly added elements. Elements
   are marked with data-nl-bound after their first pass so re-running this function never
   double-binds a listener on the same element.

   Also redirects immediately on page load (no click needed) if a visitor lands directly on a
   product page whose currently-selected variant is DE-restricted - reuses
   getSelectedVariantRedirectUrl() unchanged, since "the initially selected variant" is exactly
   what that function already reads from #variant-id-input. Accepted trade-off: since this
   script is loaded deferred, there is a brief flash of the restricted-language page before the
   redirect fires - not an instant server-side redirect - explicitly accepted by the user rather
   than solved, since a flash-free version would need a server-side (Liquid) check instead.
*/
(function () {
  var visitorCountry = null;

  // Theme settings > Restriction > "Block NL IP Customers": a Netherlands-IP visitor browsing
  // the German storefront (/de/) is treated exactly like a DE-IP/DE-language visitor (same
  // restrict_de variants, same redirect to the NL page) - but at ALL times: redirectAllowedNow()
  // skips the day/hour schedule for this case only. Mirrors ip-restrict.js's own override.
  function nlIpOnDeOverride() {
    var s = window.themeSettings || {};
    return s.blockNlIp === true && visitorCountry === 'NL' && s.currentLanguage === 'DE';
  }

  // Permanent IP range check (window.ipRange in assets/theme.js): a browser the PHP endpoint has
  // confirmed in a restricted IP range carries a permanent marker, and is redirected like a DE
  // visitor for restrict_de variants on the German storefront at ALL times - no IP country match,
  // no day/hour schedule and no Block NL toggle needed. restrict_off never gets here
  // (window.ipRange.isMarked() is false then).
  function isMarkedOnDe() {
    var s = window.themeSettings || {};
    return !!(window.ipRange && window.ipRange.isMarked()) && s.currentLanguage === 'DE';
  }

  function deRuleActive() {
    var s = window.themeSettings || {};
    // Real IP is Germany AND the storefront is currently showing the German language -
    // matches ip-restrict.js's own DE rule exactly, just re-implemented locally rather than
    // imported from that file. Or the NL-IP override above, or the permanent IP range marker.
    return (visitorCountry === 'DE' && s.currentLanguage === 'DE') || nlIpOnDeOverride() || isMarkedOnDe();
  }

  // Mode check (restrict_mode must be 'redirect_nl') + the shared day/hour window -
  // used by every entry point below. No priority arbitration needed anymore: since
  // restrict_mode is a single 3-way toggle, "restrict_ip" and "redirect_nl" can never
  // both be active at once. The actual hour-window math is shared via
  // window.isWithinShopHourWindow() (assets/theme.js) - this function only owns the
  // redirect_nl mode check, which is specific to this file.
  function redirectAllowedNow() {
    var s = window.themeSettings || {};
    if (!deRuleActive()) return false;
    if (s.restrictMode !== 'redirect_nl') return false;
    if (nlIpOnDeOverride()) return true; // "Block NL IP Customers": schedule ignored
    if (isMarkedOnDe()) return true;     // permanent IP range marker: schedule ignored
    return window.isWithinShopHourWindow(s.restrictionDays);
  }

  function getVariantData() {
    var el = document.getElementById('variant-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent).variants;
    } catch (e) {
      return null;
    }
  }

  // Collection-grid cards / PDP variant-swiper cards - anything carrying its own
  // data-restrict-de + data-nl-url on a "...btn-wrap" element.
  function applyWrapRedirects() {
    if (!redirectAllowedNow()) return;

    document.querySelectorAll('[data-restrict-de="true"][data-nl-url]:not([data-nl-bound])').forEach(function (wrap) {
      wrap.setAttribute('data-nl-bound', '1');
      var url = wrap.dataset.nlUrl;
      if (!url) return;

      // Swap the add-to-cart button for the "Meer Info" link. Both are already rendered
      // server-side (Liquid can't know who's restricted at render time - that's only ever
      // known client-side, from the live IP check) - the info link is a real <a href> pointing
      // at this variant's own NL/default page, so no click-interception is needed for it,
      // unlike the button, which needs the add-to-cart hook removed so theme.js's delegated
      // handler no longer recognizes it.
      var btn = wrap.querySelector('[data-add-variant]');
      var infoLink = wrap.querySelector('[data-nl-more-info]');
      if (btn) {
        btn.style.display = 'none';
        btn.removeAttribute('data-add-variant');
      }
      if (infoLink) infoLink.style.display = '';

      // Clicking the variant itself, not just the button: collection-grid image/name links,
      // or the PDP swiper's own clickable card (which normally just switches the selected
      // variant in place with no navigation). The info link is already a correct real link,
      // so it's excluded here to avoid double-handling it.
      var card = wrap.closest('.product-card, .variant-card');
      if (card) {
        card.querySelectorAll('a[href]').forEach(function (link) {
          if (link === infoLink) return;
          link.addEventListener('click', function (e) {
            e.preventDefault();
            window.location.href = url;
          });
        });
        if (card.classList.contains('variant-card__trigger')) {
          card.addEventListener('click', function () {
            window.location.href = url;
          });
        }
      }
    });
  }

  // PDP's single "Toevoegen" button (product-single__btn / #add-to-cart-btn): always reflects
  // whichever variant is currently selected (#variant-id-input), same source ip-restrict.js
  // already reads (#variant-data).
  function getSelectedVariantRedirectUrl(form) {
    if (!redirectAllowedNow()) return null;

    form = form || document.getElementById('add-to-cart-form');
    if (!form) return null;
    var handle = form.dataset.productHandle;
    if (!handle) return null;

    var variants = getVariantData();
    if (!variants) return null;
    var vidEl = document.getElementById('variant-id-input');
    var selectedId = vidEl ? Number(vidEl.value) : null;
    var variant = variants.find(function (v) { return v.id === selectedId; });
    if (!variant || variant.restrict_de !== true) return null;

    return '/products/' + handle + '?variant=' + variant.id;
  }

  // Primary interception: the click on the button itself, in the capture phase. Preventing the
  // click's own default action (submit the nearest form) means the "submit" event this button
  // would have triggered never fires at all - the most reliable way to beat theme.js's own
  // submit handler, rather than relying on submit-event capture/bubble ordering alone.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#add-to-cart-btn');
    if (!btn) return;
    var url = getSelectedVariantRedirectUrl();
    if (!url) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    window.location.href = url;
  }, true);

  // Fallback interception: a capture-phase listener on document runs before theme.js's own
  // bubble-phase submit handler registered directly on the form, so this still catches a
  // submission that reaches the form via another path (e.g. pressing Enter in the qty field)
  // without a click on the button itself.
  document.addEventListener('submit', function (e) {
    if (!e.target || e.target.id !== 'add-to-cart-form') return;
    var url = getSelectedVariantRedirectUrl(e.target);
    if (!url) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    window.location.href = url;
  }, true);

  window.applyRedirectNl = applyWrapRedirects;

  // Direct-landing redirect: if the currently selected variant is restricted, leave for its NL
  // page. Returns true when a redirect has started (now or earlier) - callers must then NOT call
  // finishRedirectCheck() and need not wire up interactions on a page that is being left.
  var redirecting = false;
  function redirectIfRestrictedLanding() {
    if (redirecting) return true;
    var url = getSelectedVariantRedirectUrl();
    if (!url) return false;
    redirecting = true;
    window.location.replace(url);
    return true;
  }

  if (window.beginRedirectCheck) window.beginRedirectCheck();

  // Marked browser (permanent IP range marker): decided right now, without waiting for the
  // country lookup below.
  var markedAtStart = isMarkedOnDe();
  if (markedAtStart && redirectIfRestrictedLanding()) return;
  if (markedAtStart) applyWrapRedirects();

  // A marker that appears after load (the PHP endpoint answered "restricted", or another tab
  // wrote it) takes effect immediately on this page too.
  document.addEventListener('vp:ip-marked', function () {
    if (redirectIfRestrictedLanding()) return;
    applyWrapRedirects();
  });

  window.getVisitorCountry().then(function (country) {
    visitorCountry = country;

    // Direct-landing redirect: check this before binding any click listeners, since if this
    // fires there is no point wiring up interactions on a page we are about to leave.
    if (redirectIfRestrictedLanding()) return; // Deliberately no finishRedirectCheck() call here - a redirect is starting.

    if (window.finishRedirectCheck) window.finishRedirectCheck();
    applyWrapRedirects();
  });
})();